import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact trial-end countdown pill.
 *
 * Deliberately quiet — it only appears in the final 3 days of the trial so
 * agents on a healthy trial (or paid subscription, or admin) never see it.
 * Colour intensifies as the countdown closes (blue → amber → red for last
 * day) so it earns attention without shouting.
 *
 * Complements — never replaces — the SubscriptionBanner at the top of the
 * Layout. The banner explains the state in a full sentence; the badge is a
 * glanceable "how long do I have?" indicator.
 */
const TrialCountdownBadge = () => {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status`, { withCredentials: true });
        if (alive) setState(data);
      } catch (_) { /* silent — user is logged out or endpoint unavailable */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!state) return null;
  if (state.status !== 'trial') return null;
  if ((state.days_remaining ?? 99) > 3) return null;

  const days = Math.max(0, state.days_remaining || 0);
  const tone = days <= 1
    ? 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200'
    : days <= 2
    ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
    : 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';

  const label = days === 0
    ? 'Trial ends today'
    : days === 1
    ? '1 day of trial left'
    : `${days} days of trial left`;

  return (
    <Link
      to="/billing"
      data-testid="trial-countdown-badge"
      title="Your trial is almost up — tap to see plans."
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${tone} text-xs font-semibold shadow-sm transition-colors`}
    >
      <Clock className="h-3.5 w-3.5" />
      <span data-testid="trial-countdown-label">{label}</span>
      <Sparkles className="h-3 w-3 opacity-70" />
    </Link>
  );
};

export default TrialCountdownBadge;
